// Karten-Overlays für Self-Service-Korridore
// =========================================
// Für 3PBR sind Route, Korridor-Umriss und die Routing-BEREICHE fest ins
// Bundle kompiliert (apps/rider/src/data/*.geojson). Ein selbst gebauter
// Korridor kann das naturgemäß nicht — seine GeoJSONs entstehen erst beim
// Build auf dem Server. Dieses Modul holt sie einmal über
// GET /api/corridors/region/:slug/overlays und legt sie in IndexedDB ab,
// damit sie danach auch offline (auf dem Rad, ohne Netz) da sind.
//
// Wichtig ist vor allem `naviSegments`: lange Korridore werden serverseitig
// in Bereiche zerlegt (ein Routing-Graph je ~450 km), und „Route berechnen"
// funktioniert nur INNERHALB eines Bereichs. Diese Grenzen müssen auf der
// Karte sichtbar sein — genau wie bei 3PBR.

import { authFetch } from './auth';
import { withDb } from './storage/db';
import type { RegionMapData } from '../data/corridorData';

export interface CorridorOverlays extends RegionMapData {
  region: string;
  areas: number;
  naviActive: boolean;
  naviExpiresAt: string | null;
  cachedAt: string;
}

const cacheKey = (region: string) => `corridor-overlays:${region}`;

// In-Memory-Cache: die Karte fragt bei jedem Region-Wechsel, IndexedDB soll
// dafür nicht jedes Mal aufgemacht werden.
const mem = new Map<string, CorridorOverlays>();

function emptyFc<T>(): T {
  return { type: 'FeatureCollection', features: [] } as unknown as T;
}

export async function readCachedCorridorOverlays(
  region: string,
): Promise<CorridorOverlays | null> {
  const hit = mem.get(region);
  if (hit) return hit;
  try {
    const raw = await withDb((db) => db.get('settings', cacheKey(region)));
    if (raw && typeof raw === 'object') {
      const ov = raw as CorridorOverlays;
      mem.set(region, ov);
      return ov;
    }
  } catch {
    /* IndexedDB kaputt/voll → einfach kein Cache */
  }
  return null;
}

/**
 * Holt die Overlays vom Server und schreibt sie in den Cache. Gibt null
 * zurück, wenn es sie nicht gibt (kein Self-Service-Korridor, fremder
 * User, offline). Wirft nie — die Karte darf daran nicht scheitern.
 */
export async function fetchCorridorOverlays(
  region: string,
): Promise<CorridorOverlays | null> {
  try {
    const res = await authFetch(`/api/corridors/region/${encodeURIComponent(region)}/overlays`);
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<CorridorOverlays>;
    const segs = body.naviSegments ?? emptyFc<RegionMapData['naviSegments']>();
    const areas = typeof body.areas === 'number' ? body.areas : segs.features.length;
    const ov: CorridorOverlays = {
      region,
      areas,
      naviActive: body.naviActive === true,
      naviExpiresAt: body.naviExpiresAt ?? null,
      route: body.route ?? emptyFc<RegionMapData['route']>(),
      corridor: body.corridor ?? emptyFc<RegionMapData['corridor']>(),
      // Bei genau EINEM Bereich wäre die Bereichsgrenze deckungsgleich mit
      // dem Korridor-Umriss — dann lieber nichts zeichnen. Erst ab zwei
      // Bereichen ist die Unterteilung eine echte Information für den User.
      naviSegments: areas > 1 ? segs : emptyFc<RegionMapData['naviSegments']>(),
      cachedAt: new Date().toISOString(),
    };
    mem.set(region, ov);
    try {
      await withDb((db) => db.put('settings', ov, cacheKey(region)));
    } catch {
      /* Quota o. ä. — In-Memory reicht für diese Session */
    }
    return ov;
  } catch {
    return null;
  }
}

/** Cache-first, danach still im Hintergrund aktualisieren. */
export async function getCorridorOverlays(
  region: string,
  onUpdate?: (ov: CorridorOverlays) => void,
): Promise<CorridorOverlays | null> {
  const cached = await readCachedCorridorOverlays(region);
  const refresh = fetchCorridorOverlays(region).then((fresh) => {
    if (fresh && onUpdate) onUpdate(fresh);
    return fresh;
  });
  if (cached) return cached;
  return refresh;
}

/** Nach dem Löschen der Navigationsdaten / des Korridors aufräumen. */
export async function dropCorridorOverlays(region: string): Promise<void> {
  mem.delete(region);
  try {
    await withDb((db) => db.delete('settings', cacheKey(region)));
  } catch {
    /* egal */
  }
}
